import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "@/lib/api-client";
import { toast } from "sonner";
import type { CalendarEvent, UnscheduledItem, SchedulePlatformInput, ApiScheduledPostV2, ApiDirectUpload } from "@content-os/types";

// --- Raw API functions ---

export interface CalendarFilters {
  from: string;
  to: string;
  platform?: string;
  status?: string;
  projectId?: string;
  createdBy?: string;
  contentType?: string;
}

export const calendarApi = {
  fetchCalendarEvents: async (brandId: string, filters: CalendarFilters) => {
    const params = new URLSearchParams();
    params.set("from", filters.from);
    params.set("to", filters.to);
    if (filters.platform) params.set("platform", filters.platform);
    if (filters.status) params.set("status", filters.status);
    if (filters.projectId) params.set("projectId", filters.projectId);
    if (filters.createdBy) params.set("createdBy", filters.createdBy);

    return apiFetch<CalendarEvent[]>(`/brands/${brandId}/calendar?${params.toString()}`);
  },

  fetchUnscheduledQueue: async (brandId: string, source?: string) => {
    const params = source ? `?source=${source}` : "";
    return apiFetch<UnscheduledItem[]>(`/brands/${brandId}/calendar/unscheduled${params}`);
  },

  schedulePiece: (brandId: string, pieceId: string, platforms: SchedulePlatformInput[]) =>
    apiFetch<ApiScheduledPostV2[]>(`/brands/${brandId}/pieces/${pieceId}/schedule`, {
      method: "POST",
      body: JSON.stringify({ platforms }),
    }),

  quickPost: (
    brandId: string,
    data: {
      caption: string;
      hashtags?: string[];
      mediaUrls?: string[];
      thumbnailUrl?: string | null;
      platforms: Array<{
        platform: string;
        postType?: string | null;
        metadata?: Record<string, unknown>;
        tiktokSettings?: Record<string, unknown>;
      }>;
      scheduledFor?: string;
    },
  ) =>
    apiFetch<ApiScheduledPostV2[]>(`/brands/${brandId}/quick-post`, {
      method: "POST",
      body: JSON.stringify(data),
    }),

  publishNowPiece: (
    brandId: string,
    pieceId: string,
    platforms: Array<{ platform: string; metadata?: Record<string, unknown>; tiktokSettings?: Record<string, unknown> }>,
  ) =>
    apiFetch<ApiScheduledPostV2[]>(`/brands/${brandId}/pieces/${pieceId}/publish-now`, {
      method: "POST",
      body: JSON.stringify({ platforms }),
    }),

  reschedulePost: (brandId: string, postId: string, data: { scheduledFor?: string; metadata?: Record<string, unknown> }) =>
    apiFetch<ApiScheduledPostV2>(`/brands/${brandId}/scheduled/${postId}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),

  publishScheduledNow: (brandId: string, postId: string) =>
    apiFetch<ApiScheduledPostV2>(`/brands/${brandId}/scheduled/${postId}/publish-now`, {
      method: "POST",
    }),

  cancelPost: (brandId: string, postId: string) =>
    apiFetch<{ cancelled: boolean }>(`/brands/${brandId}/scheduled/${postId}`, {
      method: "DELETE",
    }),

  directUpload: (brandId: string, data: { mediaUrls: string[]; thumbnailUrl?: string; mediaType: string; originalName: string }) =>
    apiFetch<ApiDirectUpload>(`/brands/${brandId}/upload`, {
      method: "POST",
      body: JSON.stringify(data),
    }),
};

// --- TanStack Query hooks ---

export function useCalendarEvents(brandId: string, filters: CalendarFilters) {
  return useQuery({
    queryKey: ["calendar", brandId, filters],
    queryFn: () => calendarApi.fetchCalendarEvents(brandId, filters),
    enabled: !!brandId && !!filters.from && !!filters.to,
  });
}

export function useUnscheduledQueue(brandId: string, source?: string) {
  return useQuery({
    queryKey: ["unscheduled", brandId, source],
    queryFn: () => calendarApi.fetchUnscheduledQueue(brandId, source),
    enabled: !!brandId,
  });
}

// scheduling-config lives in use-scheduling-config.ts (queryKey
// ["scheduling-config"]). A duplicate hook here used a different query key
// (["schedulingConfig"]), so a config update never invalidated the other
// cache — removed to keep one source of truth.

export function useSchedulePiece(brandId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ pieceId, platforms }: { pieceId: string; platforms: SchedulePlatformInput[] }) =>
      calendarApi.schedulePiece(brandId, pieceId, platforms),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["calendar", brandId] });
      qc.invalidateQueries({ queryKey: ["unscheduled", brandId] });
      qc.invalidateQueries({ queryKey: ["brandPieces", brandId] });
      toast.success("Piece scheduled successfully");
    },
    onError: (e: any) => toast.error(e?.message ?? "Failed to schedule piece"),
  });
}

export function useQuickPost(brandId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof calendarApi.quickPost>[1]) =>
      calendarApi.quickPost(brandId, data),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["calendar", brandId] });
      qc.invalidateQueries({ queryKey: ["brandPieces", brandId] });
      toast.success(vars.scheduledFor ? "Post scheduled" : "Publishing now…");
    },
    onError: (err: any) => toast.error(err?.message ?? "Failed to post"),
  });
}

export function usePublishNowPiece(brandId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      pieceId,
      platforms,
    }: {
      pieceId: string;
      platforms: Array<{ platform: string; metadata?: Record<string, unknown> }>;
    }) => calendarApi.publishNowPiece(brandId, pieceId, platforms),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["calendar", brandId] });
      qc.invalidateQueries({ queryKey: ["unscheduled", brandId] });
      qc.invalidateQueries({ queryKey: ["brandPieces", brandId] });
      toast.success("Publishing now…");
    },
    onError: (err: any) => toast.error(err?.message ?? "Failed to publish"),
  });
}

export function useReschedulePost(brandId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ postId, data }: { postId: string; data: { scheduledFor?: string; metadata?: Record<string, unknown> } }) =>
      calendarApi.reschedulePost(brandId, postId, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["calendar", brandId] });
      toast.success("Post rescheduled");
    },
    onError: (e: any) => toast.error(e?.message ?? "Failed to reschedule"),
  });
}

export function usePublishScheduledNow(brandId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (postId: string) => calendarApi.publishScheduledNow(brandId, postId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["calendar", brandId] });
      toast.success("Publishing now");
    },
    // Surface the server message (e.g. NOT_READY / not connected) when present.
    onError: (e: any) => toast.error(e?.message ?? "Failed to publish"),
  });
}

export function useCancelPost(brandId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (postId: string) => calendarApi.cancelPost(brandId, postId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["calendar", brandId] });
      qc.invalidateQueries({ queryKey: ["unscheduled", brandId] });
      qc.invalidateQueries({ queryKey: ["brandPieces", brandId] });
      toast.success("Post cancelled");
    },
    onError: () => toast.error("Failed to cancel post"),
  });
}

export function useDirectUpload(brandId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { mediaUrls: string[]; thumbnailUrl?: string; mediaType: string; originalName: string }) =>
      calendarApi.directUpload(brandId, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["unscheduled", brandId] });
      toast.success("Upload complete");
    },
    onError: () => toast.error("Upload failed"),
  });
}
